import { useCallback } from "react";
import api from "@/utils/connector/ApiConnector";

export interface BookingApi {
  id: string;
  productId: string;
  customerId: string;
  coworkingId: string;
  startTime: string;
  endTime: string;
  isFullDay: boolean;
  status: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  product?: {
    id: string;
    name: string;
    type: string;
  };
  customer?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface BookableProduct {
  id: string;
  name: string;
  description?: string;
  price: string;
  type: string;
}

export function useBookings() {
  const fetchBookings = useCallback(
    async (
      token: string,
      startDate?: string,
      endDate?: string,
      productId?: string
    ) => {
      const resp = await api.get("/booking", {
        params: {
          startDate,
          endDate,
          productId,
          pageSize: 500, // Get all bookings for the calendar view
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
      return Array.isArray(raw.items) ? raw.items : [];
    },
    []
  );

  const fetchBookableProducts = useCallback(async (token: string) => {
    const resp = await api.get("/booking/products", {
      headers: { Authorization: `Bearer ${token}` },
    });
    const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
    return Array.isArray(raw) ? raw : [];
  }, []);

  const createBooking = useCallback(
    async (
      token: string,
      data: {
        productId: string;
        customerId: string;
        startTime: string;
        endTime: string;
        isFullDay?: boolean;
        notes?: string;
      }
    ) => {
      const resp = await api.post("/booking", data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return (resp && (resp as any).data) ? (resp as any).data : resp;
    },
    []
  );

  const updateBooking = useCallback(
    async (
      token: string,
      bookingId: string,
      data: {
        startTime?: string;
        endTime?: string;
        isFullDay?: boolean;
        status?: string;
        notes?: string;
      }
    ) => {
      const resp = await api.patch(`/booking/${bookingId}`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return (resp && (resp as any).data) ? (resp as any).data : resp;
    },
    []
  );

  const cancelBooking = useCallback(
    async (token: string, bookingId: string) => {
      const resp = await api.delete(`/booking/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return (resp && (resp as any).data) ? (resp as any).data : resp;
    },
    []
  );

  return {
    fetchBookings,
    fetchBookableProducts,
    createBooking,
    updateBooking,
    cancelBooking,
  };
}

